import type { PolicySpec } from '@reef/schema';
import type { ConnectionSummary, FactorySummary, WorkflowSummary } from '../api.ts';
import { SubNav } from '../components/SubNav.tsx';
import type { Route } from '../routes.ts';
import { BuilderView } from './BuilderView.tsx';
import { BuildWithLanguageView } from './BuildWithLanguageView.tsx';
import { CatalogueView } from './CatalogueView.tsx';
import { FactoryWorkflowsView } from './FactoryWorkflowsView.tsx';

/**
 * Where workflows are made and started by hand.
 *
 * Three sections and one screen that is not: the catalogue, which lists every
 * workflow the reader can see and is where the screen lands; the run form, for
 * starting one with a typed work item and reading its graph; and the builder
 * agent, for describing what you want and having it drafted. The editor is
 * the fourth and has no tab — it is reached only with a workflow id, from a
 * catalogue row or a `/workflows/:id` link, because a Builder tab that opened
 * on whichever workflow sorted first was a screen nobody had asked for.
 */
export function WorkflowsView({
  section,
  workflows,
  workflowId,
  connections,
  policies,
  skills,
  factories,
  factoryWorkflowIds,
  userId,
  go,
  onSection,
  onOpenWorkflow,
  onWorkflowsChanged,
}: {
  section: string;
  workflows: WorkflowSummary[];
  workflowId: string | null;
  connections: ConnectionSummary[];
  policies: PolicySpec[];
  skills: string[];
  factories: FactorySummary[];
  factoryWorkflowIds: string[];
  userId: string;
  go(route: Partial<Route> & { tab: Route['tab'] }, replace?: boolean): void;
  onSection(id: string): void;
  onOpenWorkflow(id: string): void;
  onWorkflowsChanged(): void;
}) {
  /*
   * An id on the route means the editor — unless the section is the run form,
   * whose `/workflows/:id/run` carries the id as its initial selection. The
   * editor is the only case with no tab to mark current, which is deliberate:
   * it is a place within the catalogue, not a sibling of it.
   */
  const shown = workflowId ? (section === 'run' ? 'run' : 'editor') : section;
  return (
    <div className="screen">
      <SubNav
        title="Workflows"
        current={shown}
        onChange={onSection}
        items={[
          { id: 'catalogue', label: 'Catalogue' },
          { id: 'run', label: 'Run a workflow' },
          { id: 'language', label: 'Build with language' },
        ]}
      />
      {shown === 'run' ? (
        <FactoryWorkflowsView
          workflows={workflows}
          factoryIds={factoryWorkflowIds}
          workflowId={workflowId}
          onSelect={(id) => go({ tab: 'build', section: 'run', workflowId: id }, true)}
        />
      ) : shown === 'language' ? (
        <BuildWithLanguageView userId={userId} onWorkflowsChanged={onWorkflowsChanged} />
      ) : shown === 'catalogue' ? (
        <CatalogueView
          workflows={workflows}
          factories={factories}
          go={go}
          onSection={onSection}
          onWorkflowsChanged={onWorkflowsChanged}
        />
      ) : (
        <BuilderView
          workflows={workflows}
          workflowId={workflowId}
          connections={connections}
          policies={policies}
          skills={skills}
          onOpenWorkflow={onOpenWorkflow}
          onWorkflowsChanged={onWorkflowsChanged}
        />
      )}
    </div>
  );
}
